/*
	"Foundation" is a set of common dependencies shared by the slices. It's
	init'd/assembled by the host app here, and then will be injected into
	each slice's factory function. Slices can then pull off whatever foundation
	deps they need via destructuring, etc.
*/

import ioOutcomeFactory from "@teaming/io-outcome";
import ioTeamFactory from "@teaming/io-team";
import ioStreamFactory from "@teaming/io-stream";
import ioUserFactory from "@teaming/io-user";
import ioGokrFactory from "@teaming/io-gokr";
import ioSocketFactory from "@teaming/io-socket";
import ioSurveyFactory from "@teaming/io-survey";
import ioMailerFactory from "@teaming/io-mailer";
import ioMeetingFactory from "@teaming/io-meeting";
import ioInboxFactory from "@teaming/io-inbox";
import configureNavigation from "./navigation";
import { makeDispatchable, appChannels } from "../appDispatch";
import { screen } from "./viewport";
import { global } from "./global";
import documentRegistryFactory from "./documentRegistry";
import ioTimelineFactory from "@teaming/io-timeline";

const foundationModules = {};

// eslint-disable-next-line max-statements
export function initFoundation( {
	app,
	authService,
	config,
	dialogs,
	i18n,
	layouts,
	sidebar,
	router,
	store,
	telemetry,
} ) {
	foundationModules.screen = screen;
	foundationModules.global = global;
	foundationModules.store = store;
	foundationModules.config = config;
	foundationModules.auth = authService;
	foundationModules.telemetry = telemetry;
	foundationModules.dialogs = dialogs;
	foundationModules.sidebar = sidebar;
	foundationModules.layouts = layouts;
	foundationModules.i18n = i18n;
	foundationModules.documentRegistry = documentRegistryFactory( authService, i18n, config );
	const ioUser = ioUserFactory( { authService, config, appChannels, } );
	const ioGokr = ioGokrFactory( { authService, config, i18n, appChannels, } );
	const ioOutcome = ioOutcomeFactory( { authService, config, } );
	const ioTeam = ioTeamFactory( { authService, config, i18n, appChannels, telemetry, ioGokr, ioUser, } );
	const ioStream = ioStreamFactory( { authService, config, i18n, appChannels, telemetry, } );
	const ioMailer = ioMailerFactory( { authService, config, appChannels, } );
	const ioMeeting = ioMeetingFactory( { authService, config, appChannels, } );
	const ioSurvey = ioSurveyFactory( { config, authService, } );
	const ioInbox = ioInboxFactory( { config, authService, } );
	const ioTimeline = ioTimelineFactory( { config, authService, } );
	foundationModules.ioUser = makeDispatchable( ioUser );
	foundationModules.ioGokr = makeDispatchable( ioGokr );
	foundationModules.ioOutcome = makeDispatchable( ioOutcome );
	foundationModules.ioTeam = makeDispatchable( ioTeam );
	foundationModules.ioStream = makeDispatchable( ioStream );
	foundationModules.ioSocket = ioSocketFactory( { config, authService, } );
	foundationModules.ioSurvey = makeDispatchable( ioSurvey );
	foundationModules.ioMailer = makeDispatchable( ioMailer );
	foundationModules.ioMeeting = makeDispatchable( ioMeeting );
	foundationModules.ioInbox = makeDispatchable( ioInbox );
	foundationModules.ioTimeline = makeDispatchable( ioTimeline );
	foundationModules.ioSocket.connectToSocket();

	function registerTranslations( messages ) {
		Object.keys( messages ).forEach( locale => {
			i18n.global.mergeLocaleMessage( locale, messages[ locale ] );
		} );
	}

	function registerComponents( components ) {
		Object.keys( components ).forEach( key => {
			const componentName = components[ key ].name || key;
			if ( !app._context.components[ componentName ] ) {
				app.component( componentName, components[ key ] );
			}
		} );
	}

	foundationModules.registerTranslations = registerTranslations;
	foundationModules.registerComponents = registerComponents;
	configureNavigation( { foundationModules, router, authService, config, } );

	app.use( {
		// eslint-disable-next-line no-unused-vars
		install( _app, options ) {
			_app.config.globalProperties.$foundation = () => foundationModules;
			_app.config.globalProperties.$config = () => foundationModules.config;
			_app.config.globalProperties.$documentRegistry = () => foundationModules.documentRegistry;
		},
	} );

	return foundationModules;
}

export function foundation() {
	return foundationModules;
}
